import { postcodeWords } from './round-config'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { directionsHref } from '../functions/lib/maps-link'
import { boundsOf, pinsOf, spreadWords, unmappable, type Pin, type PinStop } from './day-map'
import { clear, el } from './ui'
import './day-map-panel.css'

// The day's stops as dots on a real map, inside Roundly.
//
// Why here and not a link: Google Maps drops ONE pin per link. Two or more
// addresses and it plans a route instead of showing you where people are,
// which is the opposite of the question ("what street are they, how close are
// they"). Nothing here costs anything or needs an account — the street
// drawing is OpenStreetMap's, credited on the map as its licence requires,
// and each dot is the customer's own position from their postcode.
//
// The dot is the visit's NUMBER in the day, so the map and the list of visits
// under it read as the same day in the same order. Tap a dot for the name,
// the address and a way through to the customer.

// Leaflet's default marker is an image file it resolves by URL, which does
// not survive bundling. Every dot here is drawn as HTML instead, so there is
// no image to lose and the number can sit inside it.
// A visit done has no number in the day's list, so its dot is a tick; "Couldn't do it" keeps its number, in grey.
const label = (pin: Pin): string => (pin.label && !pin.done ? pin.label : pin.n === null ? '✓' : String(pin.n))
// Another person's stop by its label (D1); a done one says so only when it is done (Astra: a label is not a tick).
const named = (pin: Pin): string => (pin.label && !pin.done ? `${pin.label}. ${pin.name}` : pin.n === null ? `${pin.done ? 'Done: ' : ''}${pin.name}` : `${pin.n}. ${pin.name}`)
// Design B (Liam, 28 Oct 2026: "can we brand the map in roundly colors ie markers yellow etc?", picked B,
// https://claude.ai/artifact/PopooAP6NFpvsrRLMuAdyV): a stop still to do is a lemon pin with its number in ink; the next
// stop (and a job under way) a bigger ink pin with its number in lemon; Couldn't do it a grey pin; another person's a
// white one; a visit done a small ink dot with a lemon tick. The pin's point is on the place.
const PIN_PATH = 'M20 49C20 49 4 31 4 19a16 16 0 1 1 32 0c0 12-16 30-16 30z'
const TICK_SVG = '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
function dot(pin: Pin, just = false, next = false): L.DivIcon {
  if (pin.done) {
    // A done visit with its number (the computer's Diary numbers done jobs too, 28 Oct 2026): ink, the number in lemon and
    // a small green tick; with no number, the small ink dot with a lemon tick.
    const numbered = pin.n !== null && !pin.label
    return L.divIcon({
      className: '',
      html: numbered
        ? `<span class="daymap-dot daymap-dot-done daymap-dot-donen${just ? ' daymap-dot-just' : ''}">${pin.n}<i aria-hidden="true">${TICK_SVG}</i></span>`
        : `<span class="daymap-dot daymap-dot-done${just ? ' daymap-dot-just' : ''}">${TICK_SVG}</span>`,
      iconSize: numbered ? [30, 30] : [24, 24],
      iconAnchor: numbered ? [15, 15] : [12, 12],
      popupAnchor: [0, -12],
    })
  }
  const big = next || !!pin.live
  const kind = pin.blocked ? ' daymap-pin-blocked' : big ? ' daymap-pin-next' : pin.label ? ' daymap-pin-other' : ''
  const w = big ? 46 : 36, h = Math.round(w * 1.25)
  return L.divIcon({
    className: '',
    html: `<span class="daymap-pin${kind}${just ? ' daymap-dot-just' : ''}" style="width:${w}px;height:${h}px"><svg viewBox="0 0 40 50" width="${w}" height="${h}" aria-hidden="true"><path d="${PIN_PATH}"/></svg><b>${label(pin)}</b></span>`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h + 4],
  })
}

// The next stop: the lowest number still to do (not done, not Couldn't do it). Another person's stops (D1) are not ours.
const nextOf = (pins: Pin[]): Pin | null =>
  pins.filter((p) => !p.done && !p.blocked && !p.label && p.n !== null).sort((a, b) => (a.n ?? 0) - (b.n ?? 0))[0] ?? null

function popupHtml(pin: Pin): string {
  const esc = (s: string): string =>
    s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c)
  const lines = [`<strong>${esc(named(pin))}</strong>`]
  if (pin.address) lines.push(`<span class="daymap-pop-addr">${esc(pin.address)}</span>`)
  lines.push(`<a href="#/customer/${pin.customerId}">Open their page</a>`)
  return `<div class="daymap-pop">${lines.join('')}</div>`
}

let openPanel: HTMLElement | null = null
let openMap: L.Map | null = null

export function closeDayMap(): void {
  openMap?.remove()
  openMap = null
  openPanel?.remove()
  openPanel = null
  document.body.classList.remove('daymap-locked')
}

// Open the map. `dayWords` is the day as the diary prints it.
//
// Two homes for one map (Liam, 21 Sep 2026). With no `host` it is the panel it
// has always been: laid over the diary, the page behind it locked, dismissed
// with Escape or Done — which is the right shape on a phone. Given a `host` it
// draws INSIDE that element instead, which is how the #/map/<date> page shows
// it in a tab of its own so the diary can stay open in the other one.
//
// The drawing, the dots, the "who has no postcode" rule and the popups are the
// same either way. Only the frame differs.
export function openDayMap(rows: PinStop[], dayWords: string, host?: HTMLElement): void {
  closeDayMap()
  const inline = !!host
  const pins = pinsOf(rows)
  const missing = unmappable(rows)

  const panel = inline
    ? el('div', { class: 'daymap daymap-inline', 'aria-label': `Map of ${dayWords}` })
    : el('div', { class: 'daymap', role: 'dialog', 'aria-modal': 'true', 'aria-label': `Map of ${dayWords}` })
  const head = el('div', { class: 'daymap-head' })
  head.append(el('div', { class: 'daymap-title' }, el('strong', {}, dayWords), el('span', { class: 'daymap-sub' }, spreadWords(pins))))
  // A page of its own has the browser's own back and close; only the overlay
  // needs a way out that it provides itself.
  if (!inline) {
    const close = el('button', { type: 'button', class: 'daymap-close', 'aria-label': 'Close the map' }, 'Done')
    close.addEventListener('click', () => closeDayMap())
    head.append(close)
  }
  panel.append(head)

  const canvas = el('div', { class: 'daymap-canvas' })
  panel.append(canvas)

  // Anyone with no position is named rather than silently absent: a map that
  // is quietly missing a stop is worse than one that says which.
  if (missing.length) panel.append(notOnMapList(missing))

  if (inline) {
    host!.append(panel)
  } else {
    document.body.append(panel)
    document.body.classList.add('daymap-locked')
  }
  openPanel = panel

  if (!pins.length) {
    clear(canvas)
    canvas.append(el('p', { class: 'daymap-empty' }, 'Nobody on this day is on the map yet.'))
    return
  }

  const map = drawPins(canvas, pins)
  openMap = map
  // The next stop as a card over the map, with Directions (design B): to their exact spot when pinned, else the address.
  const next = nextOf(pins)
  const goTo = next ? directionsHref(next.link, next.address) : null
  if (next) {
    canvas.append(el('div', { class: 'daymap-next' },
      el('span', { class: 'daymap-next-n', 'aria-hidden': 'true' }, label(next)),
      el('span', { class: 'daymap-next-words' }, el('strong', {}, `Next: ${next.name}`), ...(next.address ? [el('span', {}, next.address)] : [])),
      ...(goTo ? [el('a', { href: goTo, target: '_blank', rel: 'noopener', class: 'daymap-next-go' }, 'Directions')] : [])))
  }

  // A tap on "Open their page" inside a popup is a normal hash link; the map
  // has to get out of the way or it covers the customer screen it just opened.
  if (!inline) {
    panel.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('a[href^="#/"]')) closeDayMap()
    })
  }
}

// The streets and the numbered dots, drawn into `canvas`.
function drawPins(canvas: HTMLElement, pins: Pin[], just: ReadonlySet<number> = new Set()): L.Map {
  const map = L.map(canvas, { attributionControl: true, zoomControl: true })
  // CARTO Positron was tried here (2026-09-21) as a paler base: CARTO now
  // serves anonymous requests "API KEY REQUIRED" watermark tiles — HTTP 200,
  // wrong picture, so it cannot ship. OpenStreetMap's own tiles stay, and the
  // credit stays with them: that is the licence.
  // Each picture says it comes from Roundly (its address, no page or customer in it): OpenStreetMap's tile rules ask a
  // website for this, and the round's own rule (public/_headers Referrer-Policy: same-origin) left it out, so their
  // servers answered "Access blocked: App is not following the tile usage policy" (Liam's screenshot, 28 Oct 2026).
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap',
    referrerPolicy: 'strict-origin',
  }).addTo(map)

  const box = boundsOf(pins)!
  map.fitBounds([[box.south, box.west], [box.north, box.east]])
  // The ripple plays once: the class comes off as soon as the dot is drawn, and the ripple runs as an animation of its own,
  // so a map kept and put back on the page never starts it again (Astra, 24 Oct 2026).
  if (just.size) requestAnimationFrame(() => {
    for (const d of Array.from(canvas.querySelectorAll<HTMLElement>('.daymap-dot-just'))) {
      d.classList.remove('daymap-dot-just')
      if (typeof d.animate === 'function' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        d.animate([{ boxShadow: '0 0 0 0 rgba(244, 220, 82, .8)' }, { boxShadow: '0 0 0 14px rgba(244, 220, 82, 0)' }], { duration: 300, easing: 'ease-out' })
      }
    }
  })
  // The way round, in the day's order: a dotted ink line through the stops still to do.
  const ahead = pins.filter((p) => !p.done && !p.label && p.n !== null).sort((a, b) => (a.n ?? 0) - (b.n ?? 0))
  if (ahead.length > 1) L.polyline(ahead.map((p) => [p.lat, p.lng] as [number, number]), { color: '#2b3440', weight: 4, opacity: 0.7, dashArray: '2 9', lineCap: 'round', interactive: false }).addTo(map)
  const next = nextOf(pins)
  for (const pin of pins) {
    L.marker([pin.lat, pin.lng], { icon: dot(pin, just.has(pin.id), pin === next), title: named(pin), zIndexOffset: pin === next ? 1000 : pin.done ? -100 : 0 })
      .addTo(map)
      .bindPopup(popupHtml(pin))
  }
  // The panel is appended and sized in the same frame Leaflet measures in, so
  // it can read a height of zero and draw one tile in the corner.
  setTimeout(() => map.invalidateSize(), 0)
  return map
}

// The map that sits beside the day's stops in the computer Diary's Day (Liam, 24 Oct 2026: design approved at
// https://claude.ai/artifact/GvAv9orrjczy2hBG43Dq6E). A map of its own, not the overlay's, so "Bigger map" falling back
// to the overlay never tears it down; the Diary keeps it while the same stops are on show and moves it between redraws,
// asking it to measure again (resize) whenever its space changes.
export interface DeskDayMap { holder: HTMLElement; resize: () => void; remove: () => void }
// `just`: the visits marked done since the map was last drawn; their pins ripple green once.
export function drawDeskDayMap(rows: PinStop[], just: ReadonlySet<number> = new Set()): DeskDayMap {
  const pins = pinsOf(rows)
  const missing = unmappable(rows)
  const holder = el('div', { class: 'daymap-desk' })
  const canvas = el('div', { class: 'daymap-canvas' })
  holder.append(canvas)
  // Nothing to draw: one plain line in place of an empty map, saying where a postcode goes.
  if (!pins.length) {
    canvas.remove()
    holder.classList.add('daymap-desk-empty')
    // Who and why, each name opening their page (26 Oct 2026), as the Diary's Map button says it on a phone.
    holder.append(el('p', { class: 'daymap-empty' }, rows.length ? 'Nobody on this day is on the map yet.' : 'Nothing booked on this day.'))
    if (rows.length) holder.append(el('ul', { class: 'not-on-map' }, ...missing.map((m) => el('li', {}, el('a', { href: `#/customer/${m.customerId}`, class: 'not-on-map-name' }, m.name), el('span', { class: 'muted' }, postcodeWords(m.why))))))
    return { holder, resize: () => {}, remove: () => holder.remove() }
  }
  if (missing.length) holder.append(notOnMapList(missing))
  // Leaflet measures its box as it starts, so it starts once the holder is on the page.
  let map: L.Map | null = null
  const start = (): void => { if (!map && holder.isConnected) map = drawPins(canvas, pins, just) }
  requestAnimationFrame(start)
  return {
    holder,
    resize: () => { if (map) map.invalidateSize(); else start() },
    remove: () => { map?.remove(); map = null; holder.remove() },
  }
}

// Escape and a route change dismiss the OVERLAY. They must not tear down a
// map that is the page itself — the map screen owns its own lifetime.
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && openPanel && !openPanel.classList.contains('daymap-inline')) closeDayMap()
})
window.addEventListener('hashchange', () => {
  if (openPanel && !openPanel.classList.contains('daymap-inline')) closeDayMap()
})

// Who is not on the map yet and why, each name opening their page: the same words as the Diary's "Not on the map yet"
// (26 Oct 2026), for the map beside some dots too (Sonnet's computer check: it still said "No postcode on file for 3.
// Sarah Jackson" for someone with a postcode, still being placed).
function notOnMapList(missing: { n: number | string | null; name: string; customerId: number; why: string }[]): HTMLElement {
  return el('div', { class: 'daymap-missing' }, el('p', {}, 'Not on the map yet:'),
    el('ul', { class: 'not-on-map' }, ...missing.map((m) => el('li', {}, el('a', { href: `#/customer/${m.customerId}`, class: 'not-on-map-name' }, m.n === null ? m.name : `${m.n}. ${m.name}`), el('span', { class: 'muted' }, postcodeWords(m.why))))))
}
